import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import './site-navbar.css';
import BrandLogo from './BrandLogo';

const SCROLL_THRESHOLD = 24;

const SiteNavbar = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    const onPointerDown = (event) => {
      if (navRef.current?.contains(event.target)) {
        return;
      }
      setMenuOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!isHome) {
      setScrolled(false);
      return undefined;
    }

    const onScroll = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const headerClass = [
    'site-nav-sticky',
    menuOpen && 'site-nav-sticky--menu-open',
    isHome
      ? scrolled && 'site-nav-sticky--scrolled'
      : 'site-nav-sticky--inner',
  ]
    .filter(Boolean)
    .join(' ');

  const closeMenu = () => setMenuOpen(false);

  const mobileBackdrop =
    menuOpen &&
    createPortal(
      <button
        type="button"
        className="nav-mobile-backdrop is-visible"
        aria-label="Close menu"
        onClick={closeMenu}
      />,
      document.body
    );

  return (
    <header className={headerClass}>
      {mobileBackdrop}
      <nav ref={navRef} className="mr-rupya-glass-nav" aria-label="Main navigation">
        <NavLink to="/" className="nav-brand-container" onClick={closeMenu} aria-label="Credit99 home">
          <BrandLogo className="brand-logo-img" />
        </NavLink>

        <button
          type="button"
          className={`nav-menu-toggle${menuOpen ? ' is-active' : ''}`}
          aria-expanded={menuOpen}
          aria-controls="site-nav-menu"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="nav-menu-toggle-bar" />
          <span className="nav-menu-toggle-bar" />
          <span className="nav-menu-toggle-bar" />
        </button>

        <ul
          id="site-nav-menu"
          className={`nav-routing-links${menuOpen ? ' is-open' : ''}`}
        >
          <li>
            <NavLink to="/" className="routing-item" end onClick={closeMenu}>
              Home
            </NavLink>
          </li>
          <li>
            <NavLink to="/about-us" className="routing-item" onClick={closeMenu}>
              About Us
            </NavLink>
          </li>
          <li>
            <NavLink to="/repay-loan" className="routing-item" onClick={closeMenu}>
              Repay Loan
            </NavLink>
          </li>
          <li>
            <NavLink to="/contact-us" className="routing-item" onClick={closeMenu}>
              Contact Us
            </NavLink>
          </li>
        </ul>

        <div className="nav-action-zone">
          <NavLink to="/apply-now" className="btn-nav-action-cta" onClick={closeMenu}>
            Apply Now
          </NavLink>
        </div>
      </nav>
    </header>
  );
};

export default SiteNavbar;
